// =============================================================================
// src/features/crisis/crisis-takeover-controller.ts
// Abertura/fechamento do takeover de Crise: FAB, ESC, focus-trap, init preguicoso.
//
// Proveniencia: porte FIEL de assets/crisis.js 455-499 (openCrisis/closeCrisis +
//   focus-trap) e da orquestracao de drawCharts (56-86, init preguicoso). Os SVGs
//   e o ticker precisam de layout visivel (getTotalLength/scrollWidth dao 0 com
//   display:none) — por isso medimos/desenhamos apos dois requestAnimationFrame,
//   so na primeira abertura (flag `charted`).
//
// SRP: so o ciclo abrir/fechar + foco + disparo do desenho/ticker. Os graficos,
//   o slider e o ticker vivem em modulos irmaos, injetados via construtor (DI).
// =============================================================================

import { on, qs, qsa } from '../../app/dom';
import { drawCrisisCharts } from './crisis-chart-geometry';
import { hydrateCrisisGauge } from './crisis-gauge-hydrate';
import { bindCrisisEvoSlider } from './crisis-evo-slider';
import type { CrisisTickerController } from './crisis-ticker';
import type { CrisisViewSwitch } from './crisis-view-switch';
import { asTickerView } from './crisis-view-switch';

/** Seletor de elementos focaveis para o focus-trap (assets/crisis.js 456). */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, summary, [tabindex]:not([tabindex="-1"])';

/**
 * Controla o ciclo de vida do takeover. Liga o FAB (#crisisLauncher), o botao
 * fechar e o focus-trap; na primeira abertura desenha os graficos e o ticker.
 */
export class CrisisTakeoverController {
  private charted = false; // init preguicoso dos SVGs (assets/crisis.js 19)
  private lastFocus: HTMLElement | null = null; // foco a restaurar (assets/crisis.js 20)

  constructor(
    private readonly takeover: HTMLElement,
    private readonly launcher: HTMLElement,
    private readonly ticker: CrisisTickerController,
    private readonly viewSwitch: CrisisViewSwitch,
  ) {}

  /** Liga FAB, botao fechar e focus-trap (assets/crisis.js 487-499). */
  bind(): void {
    on(this.launcher, 'click', () => this.open());
    const closeBtn = qs<HTMLElement>('#crzClose');
    if (closeBtn) on(closeBtn, 'click', () => this.close());
    on(this.takeover, 'keydown', (e) => this.onKeydown(e));
  }

  /** Abre o takeover, trava o scroll e dispara o desenho preguicoso (assets/crisis.js 463-476). */
  private open(): void {
    if (!this.takeover.hidden) return;
    this.lastFocus = document.activeElement as HTMLElement | null;
    this.takeover.hidden = false;
    document.body.classList.add('crisis-open');
    this.launcher.setAttribute('aria-expanded', 'true');
    this.scheduleLazyDraw();
    qs<HTMLElement>('#crzClose')?.focus();
  }

  /** Mede/desenha apos dois frames (layout visivel) (assets/crisis.js 471-473). */
  private scheduleLazyDraw(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.drawCharts();
        this.ticker.renderTicker(asTickerView(this.viewSwitch.view));
      });
    });
  }

  /** Desenha graficos e liga o slider uma unica vez (assets/crisis.js 56-86). */
  private drawCharts(): void {
    if (this.charted) return;
    this.charted = true;
    const geometry = drawCrisisCharts();
    if (geometry) bindCrisisEvoSlider(geometry);
    hydrateCrisisGauge(this.takeover); // sobrescreve o gauge mock com o crisis_score vivo (se houver)
  }

  /** Fecha o takeover, libera o scroll e restaura o foco (assets/crisis.js 478-485). */
  private close(): void {
    if (this.takeover.hidden) return;
    this.takeover.hidden = true;
    document.body.classList.remove('crisis-open');
    this.launcher.setAttribute('aria-expanded', 'false');
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
    else this.launcher.focus();
  }

  /** ESC fecha; Tab fica preso nos focaveis visiveis (assets/crisis.js 491-499). */
  private onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.stopPropagation();
      this.close();
      return;
    }
    if (e.key !== 'Tab') return;
    this.trapTab(e);
  }

  /** Mantem o foco no primeiro/ultimo focavel visivel (assets/crisis.js 494-498). */
  private trapTab(e: KeyboardEvent): void {
    const nodes = this.visibleFocusables();
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /** Focaveis atualmente visiveis (offsetParent) ou o ativo (assets/crisis.js 458-461). */
  private visibleFocusables(): readonly HTMLElement[] {
    return qsa<HTMLElement>(FOCUSABLE_SELECTOR, this.takeover).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }
}
